import React, { useState, useRef, useEffect, useCallback } from "react";
import IconProvider from "@/components/ui/IconProvider";
import { BubbleMenu } from "@/components/editor/BubbleMenu";
import { useSelectionManager } from "@/components/editor/hooks/useSelectionManager";
import { applyFormatting, processHTMLContent } from "@/lib/utils/textFormatting";
import { sanitizeBasicHtml, escapeHtml } from "@/lib/utils/pasteHandler";
import { useZoom } from "@/providers/ZoomProvider";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface ExpandableCellEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isVisible: boolean;
  cellRect: DOMRect | null; // Position and dimensions of the original cell
  cellWidth: number; // Fixed width to maintain
  maxWidth?: number;
  placeholder?: string;
}

const ExpandableCellEditor: React.FC<ExpandableCellEditorProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  isVisible,
  cellRect,
  cellWidth,
  maxWidth = 350,
  placeholder = "",
}) => {
  const { t } = usePlatformTranslation();
  const { zoom } = useZoom();
  const [inputValue, setInputValue] = useState(value);
  const [originalValue] = useState(value); // Store original value for cancel
  const isCancellingRef = useRef(false); // Flag to prevent onChange during cancel
  const contentChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const hiddenTextareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Text selection and bubble menu management
  const {
    bubbleMenu,
    selectedText,
    hideBubbleMenu
  } = useSelectionManager(editorRef);

  // Update input value when prop changes and sync with editor
  useEffect(() => {
    // Only update if the editor is not currently focused (user is not typing)
    if (document.activeElement !== editorRef.current) {
      setInputValue(value);
      if (editorRef.current && editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
  }, [value]);

  // Set initial content when editor becomes visible (only once on mount)
  useEffect(() => {
    if (isVisible && editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
      setInputValue(value);
    }
  }, [isVisible]);

  // Focus management when editor becomes visible
  useEffect(() => {
    if (isVisible && editorRef.current) {
      // Use a small delay to ensure the component is fully mounted
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          // Set cursor at the end of content
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(editorRef.current);
          range.collapse(false); // Collapse to end
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }, 50);
    }
  }, [isVisible]);

  // Calculate editor height based on content
  const calculateHeight = useCallback((text: string) => {
    if (!hiddenTextareaRef.current) return 80; // Default to original cell height
    
    hiddenTextareaRef.current.value = text;
    const scrollHeight = hiddenTextareaRef.current.scrollHeight;
    const minHeight = 80; // Match original cell height
    const maxHeight = Math.max(300, window.innerHeight * 0.4); // Allow significant expansion
    
    return Math.min(Math.max(scrollHeight + 16, minHeight), maxHeight); // Add padding
  }, []);

  // Calculate position for the action buttons (above the editor)
  // Adjusted for zoom - divide positions by zoom factor
  const getButtonStyle = useCallback((): React.CSSProperties => {
    if (!cellRect || !isVisible) {
      return { display: 'none' };
    }

    const buttonHeight = 32; // Height of button container
    
    return {
      position: 'fixed',
      top: `${(cellRect.top - buttonHeight - 4) / zoom}px`, // 4px gap above cell
      left: `${(cellRect.left + cellWidth - 120) / zoom}px`, // Right-aligned on the cell, 120px wide for buttons
      width: `${120 / zoom}px`,
      height: `${buttonHeight / zoom}px`,
      zIndex: 10000, // Higher than editor
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: 'hsl(var(--card))',
      border: '1px solid hsl(var(--border))',
      borderRadius: '4px',
      boxShadow: '0 2px 8px hsl(var(--foreground) / 0.1)',
      padding: '4px 8px',
    };
  }, [cellRect, isVisible, cellWidth, zoom]);

  // Calculate position based on cell rect for seamless experience
  // Adjusted for zoom - divide positions by zoom factor
  const getEditorStyle = useCallback((): React.CSSProperties => {
    if (!cellRect || !isVisible) {
      return { display: 'none' };
    }

    const editorHeight = calculateHeight(inputValue);
    const originalCellHeight = cellRect.height;
    const viewportHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    // Always start at the exact cell position for seamless experience
    let top = cellRect.top + scrollTop;
    const height = Math.max(editorHeight, originalCellHeight);
    
    // Check if we need to adjust position to prevent going off screen
    const spaceBelow = viewportHeight - (cellRect.bottom - scrollTop);
    
    // If the editor would go below the viewport and there's more space above
    if (height > spaceBelow && (cellRect.top - scrollTop) > spaceBelow) {
      // Position above the cell, but ensure it aligns at the bottom with the cell
      top = cellRect.bottom + scrollTop - height;
    }

    return {
      position: 'fixed', // Use fixed positioning for better viewport handling
      top: `${cellRect.top / zoom}px`,
      left: `${cellRect.left / zoom}px`,
      width: `${cellWidth / zoom}px`,
      height: `${height / zoom}px`,
      minHeight: `${originalCellHeight / zoom}px`, // Ensure minimum height matches cell
      zIndex: 9999, // Much higher than table sticky elements (which use 40-60)
      backgroundColor: 'hsl(var(--card))',
      border: '2px solid hsl(var(--primary))', // Clear primary border for visibility
      borderRadius: '4px', // Slightly larger radius for better visual distinction
      boxShadow: '0 4px 16px hsl(var(--foreground) / 0.15)', // More prominent shadow
      overflow: 'hidden',
      // Match the cell's font and text styling
      fontSize: '14px',
      fontFamily: 'inherit',
      lineHeight: '1.2em',
    };
  }, [cellRect, isVisible, inputValue, cellWidth, calculateHeight, zoom]);

  // Save and restore cursor position utilities
  const saveCursorPosition = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editorRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      return preCaretRange.toString().length;
    }
    return 0;
  }, []);

  const restoreCursorPosition = useCallback((position: number) => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection) return;

    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT,
      null
    );

    let currentPosition = 0;
    let node = walker.nextNode();
    
    while (node) {
      const nodeLength = node.textContent?.length || 0;
      if (currentPosition + nodeLength >= position) {
        const range = document.createRange();
        range.setStart(node, position - currentPosition);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }
      currentPosition += nodeLength;
      node = walker.nextNode();
    }
  }, []);

  // Debounced content change to prevent cursor jumping during rapid typing
  const debouncedContentChange = useCallback(() => {
    try {
      if (editorRef.current && !isCancellingRef.current) {
        const htmlContent = editorRef.current.innerHTML;
        const processedContent = processHTMLContent(htmlContent);
        
        // Update the content without cursor restoration for natural typing
        setInputValue(processedContent);
        onChange(processedContent);
      }
    } catch (error) {
      console.warn('Content change error:', error);
    }
  }, [onChange]);

  // Handle content change for contentEditable div
  const handleContentChange = useCallback(() => {
    // Clear existing timeout
    if (contentChangeTimeoutRef.current) {
      clearTimeout(contentChangeTimeoutRef.current);
    }
    
    // Debounce the content change to prevent cursor jumping
    contentChangeTimeoutRef.current = setTimeout(debouncedContentChange, 150);
  }, [debouncedContentChange]);

  // Handle formatting commands from bubble menu
  const handleFormat = useCallback((command: string, value?: string) => {
    try {
      if (editorRef.current) {
        // Save cursor position before formatting
        const cursorPosition = saveCursorPosition();
        
        const formattedContent = applyFormatting(command, value, editorRef.current);
        setInputValue(formattedContent);
        onChange(formattedContent);
        
        // Restore cursor position after formatting
        setTimeout(() => {
          restoreCursorPosition(cursorPosition);
        }, 0);
        
        // Hide bubble menu after formatting
        hideBubbleMenu();
      }
    } catch (error) {
      console.warn('Formatting error:', error);
      hideBubbleMenu();
    }
  }, [onChange, hideBubbleMenu, saveCursorPosition, restoreCursorPosition]);

  // Handle paste events for contentEditable div
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    const html = e.clipboardData.getData("text/html");
    const plainText = e.clipboardData.getData("text/plain");
    
    let contentToInsert = "";
    
    if (html) {
      // If HTML is available, sanitize it
      contentToInsert = sanitizeBasicHtml(html);
    } else {
      // If only plain text, escape HTML
      contentToInsert = escapeHtml(plainText);
    }
    
    // Clean up extra whitespace and normalize line breaks
    contentToInsert = contentToInsert
      .replace(/\r\n/g, "<br>")
      .replace(/\r/g, "<br>")
      .replace(/\n/g, "<br>")
      .replace(/(<br\s*\/?>){3,}/g, "<br><br>"); // Max 2 consecutive line breaks
    
    // Insert content at cursor position
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      
      const div = document.createElement('div');
      div.innerHTML = contentToInsert;
      const fragment = document.createDocumentFragment();
      while (div.firstChild) {
        fragment.appendChild(div.firstChild);
      }
      
      range.insertNode(fragment);
      
      // Update content
      handleContentChange();
    }
  }, [handleContentChange]);

  // Handle save
  const handleSave = useCallback(() => {
    onSave();
  }, [onSave]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Set cancelling flag to prevent onChange
    isCancellingRef.current = true;
    
    // Reset to original value without triggering onChange
    setInputValue(originalValue);
    if (editorRef.current) {
      editorRef.current.innerHTML = originalValue;
    }
    
    // Call cancel callback
    onCancel();
    
    // Reset flag after a delay
    setTimeout(() => {
      isCancellingRef.current = false;
    }, 100);
  }, [onCancel, originalValue]);

  // Handle key events for contentEditable div
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && e.shiftKey) {
      // Shift+Enter: Allow line break - let default behavior handle it
      return;
    } else if (e.key === 'Enter' && !e.ctrlKey) {
      // Regular Enter: Allow line break - let default behavior handle it
      return;
    } else if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [handleSave, handleCancel]);

  // Handle clicks outside the editor
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isVisible && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Check if click is on action buttons - if so, let them handle it
        const target = event.target as Element;
        const isActionButton = target.closest('.expandable-editor-actions');
        
        if (!isActionButton) {
          handleCancel(); // Cancel on click outside, don't auto-save
        }
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, handleCancel]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (contentChangeTimeoutRef.current) {
        clearTimeout(contentChangeTimeoutRef.current);
      }
    };
  }, []);

  if (!isVisible || !cellRect) {
    return null;
  }

      return (
    <>
      {/* Subtle backdrop - very light to maintain context */}
      <div 
        className="fixed inset-0 bg-foreground/[0.02]" 
        style={{ 
          zIndex: 9998 // Just below the editor
        }}
      />
      
      {/* Action buttons - positioned above the editor */}
      <div className="expandable-editor-actions" style={getButtonStyle()}>
        <button
          onClick={handleCancel}
          className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors border border-border rounded bg-card hover:bg-muted"
          title={t('platform.cards.widgets.cancel_esc')}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-2 py-1 text-xs text-primary-foreground bg-primary hover:bg-primary/90 transition-colors border border-primary rounded"
          title={t('platform.cards.widgets.save_ctrlenter')}
        >
          Save
        </button>
      </div>
      
      {/* Editor container */}
      <div
        ref={containerRef}
        style={getEditorStyle()}
        className="flex flex-col"
      >
        {/* Hidden textarea for height calculation */}
        <textarea
          ref={hiddenTextareaRef}
          className="absolute opacity-0 pointer-events-none overflow-hidden resize-none"
          style={{
            width: `${(cellWidth - 24) / zoom}px`, // Account for padding and zoom
            lineHeight: '1.2em',
            fontSize: '14px',
            fontFamily: 'inherit',
            padding: '12px',
            boxSizing: 'border-box',
          }}
          readOnly
        />
        
        {/* Main content area - matches original cell styling */}
        <div className="flex-1 flex flex-col">
          {/* ContentEditable div for rich text editing */}
          <div
            ref={editorRef}
            contentEditable
            dir="auto"
            suppressContentEditableWarning={true}
            onInput={handleContentChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            data-placeholder={placeholder}
            className="flex-1 w-full border-none outline-none bg-transparent focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none text-foreground"
            style={{
              minHeight: cellRect?.height ? `${(cellRect.height - 16) / zoom}px` : '64px', // Match original cell height minus padding, adjusted for zoom
              lineHeight: '1.2em',
              fontSize: '14px',
              fontFamily: 'inherit',
              padding: '12px', // Match table cell padding
              wordWrap: 'break-word',
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
            }}
          />
        </div>
      </div>
      
      {/* BubbleMenu for text formatting */}
      <BubbleMenu
        isVisible={bubbleMenu.isVisible}
        position={bubbleMenu.position}
        onFormat={handleFormat}
        selectedText={selectedText}
      />
    </>
  );
};

export default ExpandableCellEditor;
